import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Link, useSearchParams } from 'react-router-dom';
import { useUser } from '../context';
import { StripePricingTableEmbed } from './StripePricingTableEmbed';
import { getStudentLimitForPlan } from './studentLimit';

/**
 * Stripe Pricing Table embed values come from `GET /api/billing/config`
 * (server picks first-time vs returning table from `user.billing`).
 *
 * Confirmation redirect (Dashboard → Confirmation page):
 *   http://localhost:3000/account/billing?session_id={CHECKOUT_SESSION_ID}
 */

type BillingConfig = {
  pricingTableId: string;
  publishableKey: string;
  returningCustomer?: boolean;
};

type CheckoutConfirmResult = {
  success: boolean;
  status: string;
  paymentStatus: string;
  userId: string | null;
  pricePlan: string | null;
  pricePlanName: string | null;
  pricePlanAt: string | null;
  productName: string | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
};

type BillingPaymentActivity = {
  id: string;
  amount: number;
  currency: string;
  status: string | null;
  createdAt: string;
  description: string | null;
  invoicePdf: string | null;
  hostedInvoiceUrl: string | null;
};

type BillingPaymentMethod = {
  id: string | null;
  type: string;
  label: string;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  funding: string | null;
  bankName: string | null;
  email: string | null;
};

type BillingOverview = {
  hasSubscription: boolean;
  plan: {
    priceId: string | null;
    name: string | null;
    startedAt: string | null;
  };
  subscription: {
    id: string | null;
    status: string | null;
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: string | null;
    nextPaymentAt: string | null;
    accessUntil: string | null;
  };
  paymentMethod: BillingPaymentMethod | null;
  payments: BillingPaymentActivity[];
};

type ConfirmStatus =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'success'; result: CheckoutConfirmResult }
  | { kind: 'failed'; result: CheckoutConfirmResult }
  | { kind: 'error'; message: string };

type ConfigStatus =
  | { kind: 'loading' }
  | { kind: 'ready'; config: BillingConfig }
  | { kind: 'error'; message: string };

type OverviewStatus =
  | { kind: 'loading' }
  | { kind: 'ready'; overview: BillingOverview }
  | { kind: 'error'; message: string };

const formatPlanDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatMoney = (amountCents: number, currency: string): string => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
    }).format(amountCents / 100);
  } catch {
    return `${(amountCents / 100).toFixed(2)} ${currency}`;
  }
};

const AccountBillingSection = () => {
  const { user, refreshUser } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [confirmStatus, setConfirmStatus] = useState<ConfirmStatus>({ kind: 'idle' });
  const [configStatus, setConfigStatus] = useState<ConfigStatus>({ kind: 'loading' });
  const [overviewStatus, setOverviewStatus] = useState<OverviewStatus>({ kind: 'loading' });
  const [cancelOpen, setCancelOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [schoolPublicId, setSchoolPublicId] = useState<string | null>(null);
  const [studentCount, setStudentCount] = useState<number | null>(null);
  const confirmedSessionRef = useRef<string | null>(null);

  const clientReferenceId = user?.id?.trim() || undefined;
  const customerEmail = user?.email?.trim() || undefined;
  const sessionId = searchParams.get('session_id')?.trim() || null;

  const loadOverview = useCallback(async (opts?: { soft?: boolean }) => {
    if (!opts?.soft) {
      setOverviewStatus({ kind: 'loading' });
    }
    try {
      const res = await fetch('/api/billing/overview', { credentials: 'include' });
      const data = (await res.json().catch(() => null)) as
        | (BillingOverview & { error?: string })
        | null;
      if (!res.ok || !data) {
        setOverviewStatus({
          kind: 'error',
          message: data?.error ?? `Could not load billing (${res.status})`,
        });
        return;
      }
      setOverviewStatus({ kind: 'ready', overview: data });
    } catch {
      setOverviewStatus({
        kind: 'error',
        message: 'Could not load billing',
      });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const res = await fetch('/api/billing/config', { credentials: 'include' });
        const data = (await res.json().catch(() => null)) as
          | (BillingConfig & { error?: string; returningCustomer?: boolean })
          | null;

        if (cancelled) return;

        if (!res.ok || !data?.pricingTableId || !data?.publishableKey) {
          setConfigStatus({
            kind: 'error',
            message: data?.error ?? `Could not load billing config (${res.status})`,
          });
          return;
        }

        setConfigStatus({
          kind: 'ready',
          config: {
            pricingTableId: data.pricingTableId,
            publishableKey: data.publishableKey,
            returningCustomer: Boolean(data.returningCustomer),
          },
        });
      } catch {
        if (!cancelled) {
          setConfigStatus({
            kind: 'error',
            message: 'Could not load billing config',
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.stripeCustomerId]);

  useEffect(() => {
    void loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (!sessionId || !user?.id) return;
    if (confirmedSessionRef.current === sessionId) return;

    let cancelled = false;
    confirmedSessionRef.current = sessionId;
    setConfirmStatus({ kind: 'loading' });

    void (async () => {
      try {
        const res = await fetch(
          `/api/billing/checkout-session?session_id=${encodeURIComponent(sessionId)}`,
          { credentials: 'include' },
        );
        const data = (await res.json().catch(() => null)) as
          | (CheckoutConfirmResult & { error?: string })
          | null;

        if (cancelled) return;

        if (!res.ok) {
          setConfirmStatus({
            kind: 'error',
            message: data?.error ?? `Could not verify payment (${res.status})`,
          });
          return;
        }

        if (!data) {
          setConfirmStatus({
            kind: 'error',
            message: 'Could not verify payment',
          });
          return;
        }

        if (data.success) {
          setConfirmStatus({ kind: 'success', result: data });
          await refreshUser();
          await loadOverview();
        } else {
          setConfirmStatus({ kind: 'failed', result: data });
        }
      } catch {
        if (!cancelled) {
          setConfirmStatus({
            kind: 'error',
            message: 'Could not verify payment',
          });
        }
      } finally {
        if (!cancelled) {
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete('session_id');
              return next;
            },
            { replace: true },
          );
        }
      }
    })();

    return () => {
      cancelled = true;
      if (confirmedSessionRef.current === sessionId) {
        confirmedSessionRef.current = null;
      }
    };
  }, [sessionId, user?.id, refreshUser, setSearchParams, loadOverview]);

  const handleCancelSubscription = useCallback(async () => {
    setCanceling(true);
    setCancelError(null);
    try {
      const res = await fetch('/api/billing/cancel', {
        method: 'POST',
        credentials: 'include',
      });
      const data = (await res.json().catch(() => null)) as { error?: string } | null;
      if (!res.ok) {
        throw new Error(data?.error ?? `Cancel failed (${res.status})`);
      }
      setCancelOpen(false);
      await refreshUser();
      await loadOverview();
    } catch (e) {
      setCancelError(e instanceof Error ? e.message : 'Cancel failed');
    } finally {
      setCanceling(false);
    }
  }, [loadOverview, refreshUser]);

  const openBillingPortal = useCallback(
    async (body: {
      paymentMethodUpdate?: boolean;
      subscriptionUpdate?: boolean;
    } = {}) => {
      setPortalLoading(true);
      setPortalError(null);
      try {
        const res = await fetch('/api/billing/portal', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = (await res.json().catch(() => null)) as
          | { url?: string; error?: string }
          | null;
        if (!res.ok || !data?.url) {
          throw new Error(
            data?.error ?? `Could not open billing portal (${res.status})`,
          );
        }
        window.location.assign(data.url);
      } catch (e) {
        setPortalError(
          e instanceof Error ? e.message : 'Could not open billing portal',
        );
        setPortalLoading(false);
      }
    },
    [],
  );

  const handleUpdatePaymentMethod = useCallback(() => {
    void openBillingPortal({ paymentMethodUpdate: true });
  }, [openBillingPortal]);

  const handleChangePlan = useCallback(() => {
    void openBillingPortal({ subscriptionUpdate: true });
  }, [openBillingPortal]);

  const handleViewAllInvoices = useCallback(() => {
    // Plain portal session — invoice history is on the portal home when enabled.
    void openBillingPortal();
  }, [openBillingPortal]);

  const overview =
    overviewStatus.kind === 'ready' ? overviewStatus.overview : null;
  const hasSubscription = Boolean(overview?.hasSubscription);
  const planName =
    overview?.plan.name ||
    user?.pricePlanName ||
    (confirmStatus.kind === 'success'
      ? confirmStatus.result.productName || confirmStatus.result.pricePlanName
      : null);
  const planStartedAt = formatPlanDate(
    overview?.plan.startedAt || user?.pricePlanAt,
  );
  const accessUntil = formatPlanDate(overview?.subscription.accessUntil);
  const nextPaymentAt = formatPlanDate(overview?.subscription.nextPaymentAt);
  const cancelAtPeriodEnd = Boolean(overview?.subscription.cancelAtPeriodEnd);
  const paymentMethod = overview?.paymentMethod ?? null;
  const payments = overview?.payments ?? [];
  const returningCustomer =
    configStatus.kind === 'ready' && Boolean(configStatus.config.returningCustomer);

  // Re-check student quota whenever the effective plan changes (checkout / portal return).
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const schoolRes = await fetch('/api/school', { credentials: 'include' });
        if (!schoolRes.ok) {
          if (!cancelled) {
            setSchoolPublicId(null);
            setStudentCount(null);
          }
          return;
        }
        const schoolData = (await schoolRes.json()) as {
          school: { schoolId?: string } | null;
        };
        const publicId = schoolData.school?.schoolId?.trim() || null;
        if (!publicId) {
          if (!cancelled) {
            setSchoolPublicId(null);
            setStudentCount(null);
          }
          return;
        }

        const studentsRes = await fetch('/api/students', { credentials: 'include' });
        if (!studentsRes.ok) {
          if (!cancelled) {
            setSchoolPublicId(publicId);
            setStudentCount(null);
          }
          return;
        }
        const studentsData = (await studentsRes.json()) as {
          students?: unknown[];
        };
        if (cancelled) return;
        setSchoolPublicId(publicId);
        setStudentCount(
          Array.isArray(studentsData.students) ? studentsData.students.length : 0,
        );
      } catch {
        if (!cancelled) {
          setSchoolPublicId(null);
          setStudentCount(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [planName, confirmStatus.kind, user?.pricePlanName]);

  const studentLimit = useMemo(
    () => getStudentLimitForPlan(planName),
    [planName],
  );

  const overStudentQuota =
    studentCount !== null &&
    studentLimit !== null &&
    studentCount > studentLimit;

  return (
    <section
      className="rounded-3xl border-[3px] border-ink/15 bg-white/95 p-6 shadow-[4px_6px_0_rgba(44,44,44,0.08)]"
      aria-labelledby="billing-section-heading"
    >
      <h2
        id="billing-section-heading"
        className="font-display text-xl font-semibold text-ink"
      >
        Billing
      </h2>
      <p className="mt-2 font-sans text-sm text-ink/65">
        {hasSubscription
          ? 'Manage your school plan, payment method, and billing history.'
          : returningCustomer
            ? 'Choose a plan that fits your school. Cancel anytime.'
            : 'Choose a plan that fits your school. Enjoy 30 days free, and cancel anytime.'}
      </p>

      {confirmStatus.kind === 'loading' && (
        <p
          className="mt-4 rounded-2xl bg-skysoft/30 px-4 py-3 font-sans text-sm text-ink/80"
          role="status"
        >
          Confirming your payment…
        </p>
      )}

      {confirmStatus.kind === 'success' && (
        <p
          className="mt-4 rounded-2xl bg-grass/40 px-4 py-3 font-sans text-sm font-semibold text-ink"
          role="status"
        >
          Payment successful
          {planName ? ` — you’re on ${planName}` : ''}.
        </p>
      )}

      {overStudentQuota && studentCount !== null && studentLimit !== null && (
        <div
          className="mt-4 rounded-2xl bg-doghouse/20 px-4 py-3 font-sans text-sm text-ink"
          role="alert"
        >
          <p className="font-semibold">You&apos;re over your student quota</p>
          <p className="mt-1 text-ink/80">
            Your school has {studentCount} students, but{' '}
            {planName ? (
              <>
                <span className="font-semibold">{planName}</span> allows
              </>
            ) : (
              'your current plan allows'
            )}{' '}
            {studentLimit}. Check-in and check-out are disabled until you upgrade
            your plan or remove students.
          </p>
          {schoolPublicId ? (
            <p className="mt-3">
              <Link
                to={`/attendance/${encodeURIComponent(schoolPublicId)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-white/80 px-4 py-2 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-white hover:ring-ink/20"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4 shrink-0 text-ink/70" aria-hidden />
                Open attendance page
              </Link>
            </p>
          ) : null}
        </div>
      )}

      {confirmStatus.kind === 'failed' && (
        <p
          className="mt-4 rounded-2xl bg-doghouse/25 px-4 py-3 font-sans text-sm text-ink"
          role="alert"
        >
          Payment was not completed (status: {confirmStatus.result.status},{' '}
          {confirmStatus.result.paymentStatus}). You can try again below.
        </p>
      )}

      {confirmStatus.kind === 'error' && (
        <p
          className="mt-4 rounded-2xl bg-doghouse/25 px-4 py-3 font-sans text-sm text-ink"
          role="alert"
        >
          {confirmStatus.message}
        </p>
      )}

      {overviewStatus.kind === 'loading' && confirmStatus.kind !== 'loading' && (
        <p className="mt-6 font-sans text-sm text-ink/60" role="status">
          Loading billing…
        </p>
      )}

      {overviewStatus.kind === 'error' && (
        <p
          className="mt-6 rounded-2xl bg-doghouse/25 px-4 py-3 font-sans text-sm text-ink"
          role="alert"
        >
          {overviewStatus.message}
        </p>
      )}

      {hasSubscription && overview && (
        <div className="mt-6 space-y-6">
          {cancelAtPeriodEnd && (
            <p
              className="rounded-2xl bg-amber-100/80 px-4 py-3 font-sans text-sm text-ink"
              role="status"
            >
              Cancellation scheduled. You’ll keep access
              {accessUntil ? ` until ${accessUntil}` : ' until the end of the billing period'}
              . No further charges will be made.
            </p>
          )}

          <div className="rounded-2xl bg-skysoft/25 p-4">
            <p className="font-sans text-xs font-semibold uppercase tracking-wider text-ink/55">
              Current plan
            </p>
            <p className="mt-1 font-display text-2xl font-semibold text-ink">
              {planName || 'Your plan'}
            </p>
            <p className="mt-1 font-sans text-sm text-ink/65">
              {overview.subscription.status
                ? `Status: ${overview.subscription.status}`
                : null}
              {planStartedAt ? ` · since ${planStartedAt}` : null}
            </p>
            <p className="mt-2 font-sans text-sm text-ink/75">
              {cancelAtPeriodEnd ? (
                <>
                  Access until:{' '}
                  <span className="font-semibold text-ink">
                    {accessUntil || 'end of billing period'}
                  </span>
                </>
              ) : (
                <>
                  Next payment:{' '}
                  <span className="font-semibold text-ink">
                    {nextPaymentAt || '—'}
                  </span>
                </>
              )}
            </p>
          </div>

          <div>
            <h3 className="font-display text-lg font-semibold text-ink">
              Payment method
            </h3>
            {paymentMethod ? (
              <p className="mt-2 font-sans text-sm text-ink/80">
                {paymentMethod.label}
              </p>
            ) : (
              <p className="mt-2 font-sans text-sm text-ink/60">
                No payment method on file yet.
              </p>
            )}
            <button
              type="button"
              disabled={portalLoading}
              onClick={() => handleUpdatePaymentMethod()}
              className="mt-3 rounded-full px-4 py-2 font-sans text-sm font-semibold text-ink ring-2 ring-ink/15 transition hover:bg-skysoft/40 disabled:opacity-50"
            >
              {portalLoading ? 'Opening Stripe…' : 'Update payment method'}
            </button>
          </div>

          <div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-display text-lg font-semibold text-ink">
                Recent payments
              </h3>
              <button
                type="button"
                disabled={portalLoading}
                onClick={() => void handleViewAllInvoices()}
                className="font-sans text-sm font-semibold text-ink/70 underline-offset-2 hover:text-ink hover:underline disabled:opacity-50"
              >
                {portalLoading ? 'Opening Stripe…' : 'Show more'}
              </button>
            </div>
            {payments.length === 0 ? (
              <p className="mt-2 font-sans text-sm text-ink/60">
                No payment activity yet.
              </p>
            ) : (
              <ul className="mt-3 divide-y divide-ink/10 rounded-2xl border-2 border-ink/10">
                {payments.map((payment) => (
                  <li
                    key={payment.id}
                    className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 font-sans text-sm"
                  >
                    <div>
                      <p className="font-semibold text-ink">
                        {formatMoney(payment.amount, payment.currency)}
                        {payment.status ? (
                          <span className="ml-2 font-normal text-ink/55">
                            {payment.status}
                          </span>
                        ) : null}
                      </p>
                      <p className="text-ink/60">
                        {formatPlanDate(payment.createdAt)}
                        {payment.description ? ` · ${payment.description}` : ''}
                      </p>
                    </div>
                    {(payment.hostedInvoiceUrl || payment.invoicePdf) && (
                      <a
                        href={payment.hostedInvoiceUrl || payment.invoicePdf || '#'}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-full px-3 py-1.5 text-xs font-semibold text-ink ring-1 ring-ink/15 hover:bg-skysoft/40"
                      >
                        Invoice
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              disabled={portalLoading}
              onClick={() => handleChangePlan()}
              className="rounded-full bg-skysoft/70 px-5 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-skysoft disabled:opacity-50"
            >
              {portalLoading ? 'Opening Stripe…' : 'Change plan'}
            </button>
            {!cancelAtPeriodEnd && (
              <button
                type="button"
                onClick={() => {
                  setCancelError(null);
                  setCancelOpen(true);
                }}
                className="rounded-full px-5 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/15 transition hover:bg-doghouse/20"
              >
                Cancel subscription
              </button>
            )}
          </div>
          {portalError && (
            <p
              className="rounded-xl bg-doghouse/25 px-3 py-2 font-sans text-sm text-ink"
              role="alert"
            >
              {portalError}
            </p>
          )}
        </div>
      )}

      {!hasSubscription && overviewStatus.kind === 'ready' && (
        <>
          {configStatus.kind === 'loading' && (
            <p className="mt-6 font-sans text-sm text-ink/60" role="status">
              Loading plans…
            </p>
          )}

          {configStatus.kind === 'error' && (
            <p
              className="mt-6 rounded-2xl bg-doghouse/25 px-4 py-3 font-sans text-sm text-ink"
              role="alert"
            >
              {configStatus.message}
            </p>
          )}

          {configStatus.kind === 'ready' && (
            <div className="mt-6">
              <StripePricingTableEmbed
                config={configStatus.config}
                clientReferenceId={clientReferenceId}
                customerEmail={customerEmail}
              />
            </div>
          )}
        </>
      )}

      <Transition appear show={cancelOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[60]"
          onClose={() => {
            if (!canceling) setCancelOpen(false);
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-ink/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md rounded-3xl border-[3px] border-ink/15 bg-white p-6 shadow-[4px_8px_0_rgba(44,44,44,0.1)]">
                  <Dialog.Title className="font-display text-xl font-semibold text-ink">
                    Cancel subscription?
                  </Dialog.Title>
                  <Dialog.Description className="mt-2 font-sans text-sm text-ink/70">
                    You’ll keep access to{' '}
                    <span className="font-semibold text-ink">
                      {planName || 'your plan'}
                    </span>
                    {accessUntil ? (
                      <>
                        {' '}
                        until <span className="font-semibold text-ink">{accessUntil}</span>
                      </>
                    ) : (
                      ' until the end of the current billing period'
                    )}
                    . There is no refund for the current period, and you won’t be
                    charged again.
                  </Dialog.Description>

                  {cancelError && (
                    <p className="mt-3 rounded-xl bg-doghouse/25 px-3 py-2 font-sans text-sm text-ink">
                      {cancelError}
                    </p>
                  )}

                  <div className="mt-6 flex flex-wrap justify-end gap-3">
                    <button
                      type="button"
                      disabled={canceling}
                      onClick={() => setCancelOpen(false)}
                      className="rounded-full px-4 py-2 font-sans text-sm font-semibold text-ink ring-1 ring-ink/15 hover:bg-skysoft/40 disabled:opacity-50"
                    >
                      Keep plan
                    </button>
                    <button
                      type="button"
                      disabled={canceling}
                      onClick={() => void handleCancelSubscription()}
                      className="rounded-full bg-doghouse/80 px-4 py-2 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 hover:bg-doghouse disabled:opacity-50"
                    >
                      {canceling ? 'Canceling…' : 'Confirm cancel'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </section>
  );
};

export default AccountBillingSection;
